import type { FC } from "react";

import type { DashboardSchemaTelemetryScopeSummaryResponse } from "../../dashboard-client";

import { formatTokensWithUnit, formatUsd } from "../../utils/currency";
import { Alert } from "../common/Alert";
import { ContentCard } from "../common/ContentCard";
import { SectionHeader } from "../common/SectionHeader";
import { StatsGrid } from "../common/StatsGrid";
import { Text } from "../common/Text";

interface PlanRevisionCostSummaryProps {
  summary: DashboardSchemaTelemetryScopeSummaryResponse | null;
  loading: boolean;
  error: string | null;
  scopeId: string;
}

export const PlanRevisionCostSummary: FC<PlanRevisionCostSummaryProps> = ({
  summary,
  loading,
  error,
  scopeId,
}) => {
  if (!loading && !summary && !error) {
    return null;
  }

  const summaryValue = (
    loadingLabel: string,
    format: (s: NonNullable<typeof summary>) => string,
  ): string => {
    if (loading) {
      return loadingLabel;
    }
    if (summary) {
      return format(summary);
    }
    return "\u2014";
  };

  const items = [
    {
      className: loading || summary ? "" : "opacity-50",
      label: "Total Cost",
      value: summaryValue("Loading\u2026", (s) => formatUsd(s.costUsd)),
    },
    {
      className: loading || summary ? "" : "opacity-50",
      label: "Total Tokens",
      value: summaryValue("Loading\u2026", (s) =>
        formatTokensWithUnit(s.tokens.totalTokens),
      ),
    },
    {
      className: "hidden sm:block",
      label: "Prompt",
      value: summaryValue(
        "\u2014",
        (s) => `${s.tokens.promptTokens.toLocaleString()} tok`,
      ),
    },
    {
      className: "hidden sm:block",
      label: "Completion",
      value: summaryValue(
        "\u2014",
        (s) => `${s.tokens.completionTokens.toLocaleString()} tok`,
      ),
    },
    {
      className: "hidden sm:block",
      label: "Calls",
      value: summaryValue("\u2014", (s) => s.count.toLocaleString()),
    },
  ];

  return (
    <section>
      <SectionHeader
        title="Orchestrator LLM Costs (Plan Revision)"
        variant="label"
        icon={
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .672-3 1.5S10.343 11 12 11s3 .672 3 1.5S13.657 14 12 14m0-6V7m0 7v1m-6 4h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        }
      />
      <ContentCard>
        <div className="mb-4">
          <Text as="p" className="truncate font-mono">
            scope: plan_revision · {scopeId}
          </Text>
        </div>

        {error ? (
          <Alert variant="error">{error}</Alert>
        ) : (
          <StatsGrid items={items} columns={4} />
        )}
      </ContentCard>
    </section>
  );
};
